import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from "./ScrapingContainer.module.css";

// Componentes de iconos (puedes importar los que necesites)
const IconPlus = () => <span>+</span>;
const IconTrash = () => <span>🗑️</span>;
const IconEdit = () => <span>✏️</span>;
const IconPlay = () => <span>▶️</span>;
const IconPause = () => <span>⏸️</span>;
const IconEye = () => <span>👁️</span>;
const IconCheck = () => <span>✅</span>;
const IconClose = () => <span>❌</span>;

const ScrapingContainer = ({
    status,
    onStatusChange,
    onTemplateSave,
    onScrapingExecute,
    initialUrl = '',
    initialScreenshot = null
}) => {
    // Estados principales
    const [currentUrl, setCurrentUrl] = useState(initialUrl);
    const [screenshot, setScreenshot] = useState(initialScreenshot);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    
    // Estados del selector
    const [selectors, setSelectors] = useState([]);
    const [selectedElement, setSelectedElement] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);
    
    // Estados del template
    const [template, setTemplate] = useState({
        id: uuidv4(),
        name: 'Nuevo Template',
        description: '',
        steps: [],
        variables: {},
        conditions: []
    });
    
    // Estados del example
    const [scrapingResults, setScrapingResults] = useState([]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [executionTime, setExecutionTime] = useState(0);
    
    // Referencias
    const screenshotRef = useRef(null);
    const selectorOverlayRef = useRef(null);

    // ===== ESTADO LOADING =====
    const LoadingState = () => {
        const [urlInput, setUrlInput] = useState(currentUrl);

        const handleLoadPage = async () => {
            if (!urlInput.trim()) return;
            
            setIsLoading(true);
            setLoadingProgress(0);
            
            // Simular progreso de carga
            const progressInterval = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 200);

            try {
                // Aquí iría la lógica real de carga de página
                // Por ahora simulamos con un timeout
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                // Simular screenshot (en producción sería real)
                const mockScreenshot = `data:image/svg+xml;base64,${btoa(`
                    <svg width="800" height="600" xmlns="http://www.w3.org/2000/svg">
                        <rect width="100%" height="100%" fill="#f0f0f0"/>
                        <text x="400" y="300" text-anchor="middle" fill="#333" font-size="24">
                            Página cargada: ${urlInput}
                        </text>
                    </svg>
                `)}`;
                
                setScreenshot(mockScreenshot);
                setCurrentUrl(urlInput);
                setLoadingProgress(100);
                
                setTimeout(() => {
                    setIsLoading(false);
                    onStatusChange('selector');
                }, 500);
                
            } catch (error) {
                console.error('Error loading page:', error);
                setIsLoading(false);
            }
        };

        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingHeader}>
                    <h3>🔄 Cargando página web</h3>
                    <p>Ingresa la URL de la página que quieres analizar</p>
                </div>
                
                <div className={styles.urlInputContainer}>
                    <input
                        type="url"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        placeholder="https://ejemplo.com"
                        className={styles.urlInput}
                        disabled={isLoading}
                    />
                    <button
                        onClick={handleLoadPage}
                        disabled={isLoading || !urlInput.trim()}
                        className={styles.loadButton}
                    >
                        {isLoading ? 'Cargando...' : 'Cargar Página'}
                    </button>
                </div>

                {isLoading && (
                    <div className={styles.progressContainer}>
                        <div className={styles.progressBar}>
                            <div 
                                className={styles.progressFill}
                                style={{ width: `${loadingProgress}%` }}
                            />
                        </div>
                        <span className={styles.progressText}>{loadingProgress}%</span>
                    </div>
                )}

                {screenshot && (
                    <div className={styles.screenshotPreview}>
                        <h4>Vista previa de la página:</h4>
                        <img 
                            src={screenshot} 
                            alt="Screenshot de la página"
                            className={styles.screenshotImage}
                        />
                    </div>
                )}
            </div>
        );
    };

    // ===== ESTADO SELECTOR =====
    const SelectorState = () => {
        const [newSelector, setNewSelector] = useState({
            name: '',
            selector: '',
            prompt: '',
            type: 'text' // text, click, wait, condition
        });

        const handleElementSelect = useCallback((event) => {
            if (!isSelecting) return;
            
            event.preventDefault();
            event.stopPropagation();
            
            const element = event.target;
            const rect = element.getBoundingClientRect();
            const selector = generateSelector(element);
            
            setSelectedElement({
                element,
                rect,
                selector,
                name: `Elemento ${selectors.length + 1}`
            });
            
            setIsSelecting(false);
        }, [isSelecting, selectors.length]);

        const generateSelector = (element) => {
            // Generar selector CSS básico
            if (element.id) {
                return `#${element.id}`;
            }
            
            if (element.className) {
                const classes = element.className.split(' ').filter(c => c.trim());
                if (classes.length > 0) {
                    return `.${classes[0]}`;
                }
            }
            
            return element.tagName.toLowerCase();
        };

        const addSelector = () => {
            if (!selectedElement || !newSelector.name.trim()) return;
            
            const selector = {
                id: uuidv4(),
                name: newSelector.name,
                selector: selectedElement.selector,
                prompt: newSelector.prompt,
                type: newSelector.type,
                position: {
                    x: selectedElement.rect.x,
                    y: selectedElement.rect.y,
                    width: selectedElement.rect.width,
                    height: selectedElement.rect.height
                }
            };
            
            setSelectors(prev => [...prev, selector]);
            setSelectedElement(null);
            setNewSelector({
                name: '',
                selector: '',
                prompt: '',
                type: 'text'
            });
        };

        const removeSelector = (id) => {
            setSelectors(prev => prev.filter(s => s.id !== id));
        };

        const startSelection = () => {
            setIsSelecting(true);
        };

        return (
            <div className={styles.selectorContainer}>
                <div className={styles.selectorHeader}>
                    <h3>🎯 Seleccionar elementos</h3>
                    <p>Haz clic en los elementos que quieres extraer o interactuar</p>
                </div>

                <div className={styles.selectorContent}>
                    <div className={styles.screenshotContainer}>
                        <div className={styles.screenshotWrapper}>
                            <img 
                                ref={screenshotRef}
                                src={screenshot} 
                                alt="Página para seleccionar"
                                className={styles.screenshotImage}
                                onClick={handleElementSelect}
                                style={{ cursor: isSelecting ? 'crosshair' : 'default' }}
                            />
                            
                            {/* Overlay para mostrar selectores */}
                            <div ref={selectorOverlayRef} className={styles.selectorOverlay}>
                                {selectors.map(selector => (
                                    <div
                                        key={selector.id}
                                        className={styles.selectorBox}
                                        style={{
                                            left: selector.position.x,
                                            top: selector.position.y,
                                            width: selector.position.width,
                                            height: selector.position.height
                                        }}
                                    >
                                        <div className={styles.selectorLabel}>
                                            {selector.name}
                                        </div>
                                    </div>
                                ))}
                                
                                {selectedElement && (
                                    <div
                                        className={styles.selectedElement}
                                        style={{
                                            left: selectedElement.rect.x,
                                            top: selectedElement.rect.y,
                                            width: selectedElement.rect.width,
                                            height: selectedElement.rect.height
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.selectorControls}>
                        <div className={styles.selectionButtons}>
                            <button
                                onClick={startSelection}
                                className={`${styles.selectionButton} ${isSelecting ? styles.active : ''}`}
                            >
                                <IconEye /> Seleccionar Elemento
                            </button>
                            <button
                                onClick={() => onStatusChange('template')}
                                className={styles.continueButton}
                                disabled={selectors.length === 0}
                            >
                                Continuar al Template ({selectors.length} elementos)
                            </button>
                        </div>

                        {selectedElement && (
                            <div className={styles.selectorForm}>
                                <h4>Configurar selector</h4>
                                <input
                                    type="text"
                                    placeholder="Nombre del selector (ej: precio, título, botón)"
                                    value={newSelector.name}
                                    onChange={(e) => setNewSelector(prev => ({ ...prev, name: e.target.value }))}
                                    className={styles.selectorInput}
                                />
                                
                                <select
                                    value={newSelector.type}
                                    onChange={(e) => setNewSelector(prev => ({ ...prev, type: e.target.value }))}
                                    className={styles.selectorSelect}
                                >
                                    <option value="text">Extraer texto</option>
                                    <option value="click">Hacer clic</option>
                                    <option value="wait">Esperar</option>
                                    <option value="condition">Condición</option>
                                </select>
                                
                                <textarea
                                    placeholder="Prompt/Condición (ej: debe contener '€', debe ser visible, etc.)"
                                    value={newSelector.prompt}
                                    onChange={(e) => setNewSelector(prev => ({ ...prev, prompt: e.target.value }))}
                                    className={styles.selectorTextarea}
                                    rows={3}
                                />
                                
                                <div className={styles.selectorActions}>
                                    <button onClick={addSelector} className={styles.addButton}>
                                        <IconPlus /> Añadir Selector
                                    </button>
                                    <button 
                                        onClick={() => setSelectedElement(null)} 
                                        className={styles.cancelButton}
                                    >
                                        <IconClose /> Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={styles.selectorsList}>
                            <h4>Selectores configurados ({selectors.length})</h4>
                            {selectors.map(selector => (
                                <div key={selector.id} className={styles.selectorItem}>
                                    <div className={styles.selectorInfo}>
                                        <strong>{selector.name}</strong>
                                        <span className={styles.selectorType}>{selector.type}</span>
                                        <span className={styles.selectorPath}>{selector.selector}</span>
                                    </div>
                                    <button
                                        onClick={() => removeSelector(selector.id)}
                                        className={styles.removeButton}
                                    >
                                        <IconTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ===== ESTADO TEMPLATE =====
    const TemplateState = () => {
        const [editingStep, setEditingStep] = useState(null);
        const [newStep, setNewStep] = useState({
            name: '',
            type: 'action', // action, condition, loop, variable
            action: '',
            parameters: {},
            conditions: []
        });

        const addStep = () => {
            if (!newStep.name.trim()) return;
            
            const step = {
                id: uuidv4(),
                ...newStep,
                order: template.steps.length
            };
            
            setTemplate(prev => ({
                ...prev,
                steps: [...prev.steps, step]
            }));
            
            setNewStep({
                name: '',
                type: 'action',
                action: '',
                parameters: {},
                conditions: []
            });
        };

        const removeStep = (id) => {
            setTemplate(prev => ({
                ...prev,
                steps: prev.steps.filter(s => s.id !== id)
            }));
        };

        const updateStep = (id, updates) => {
            setTemplate(prev => ({
                ...prev,
                steps: prev.steps.map(s => s.id === id ? { ...s, ...updates } : s)
            }));
        };

        const moveStep = (id, direction) => {
            setTemplate(prev => {
                const steps = [...prev.steps];
                const index = steps.findIndex(s => s.id === id);
                if (index === -1) return prev;
                
                const newIndex = direction === 'up' ? index - 1 : index + 1;
                if (newIndex < 0 || newIndex >= steps.length) return prev;
                
                [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
                return { ...prev, steps };
            });
        };

        const saveTemplate = () => {
            onTemplateSave(template);
        };

        return (
            <div className={styles.templateContainer}>
                <div className={styles.templateHeader}>
                    <h3>📋 Editor de Template</h3>
                    <p>Configura los pasos del scraping y las condiciones</p>
                </div>

                <div className={styles.templateContent}>
                    <div className={styles.templateInfo}>
                        <input
                            type="text"
                            placeholder="Nombre del template"
                            value={template.name}
                            onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                            className={styles.templateNameInput}
                        />
                        <textarea
                            placeholder="Descripción del template"
                            value={template.description}
                            onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                            className={styles.templateDescriptionInput}
                            rows={3}
                        />
                    </div>

                    <div className={styles.stepsContainer}>
                        <h4>Pasos del scraping ({template.steps.length})</h4>
                        
                        <div className={styles.stepsList}>
                            {template.steps.map((step, index) => (
                                <div key={step.id} className={styles.stepItem}>
                                    <div className={styles.stepHeader}>
                                        <span className={styles.stepNumber}>{index + 1}</span>
                                        <strong>{step.name}</strong>
                                        <span className={styles.stepType}>{step.type}</span>
                                    </div>
                                    
                                    <div className={styles.stepActions}>
                                        <button onClick={() => setEditingStep(step.id)}>
                                            <IconEdit />
                                        </button>
                                        <button onClick={() => moveStep(step.id, 'up')} disabled={index === 0}>
                                            ↑
                                        </button>
                                        <button onClick={() => moveStep(step.id, 'down')} disabled={index === template.steps.length - 1}>
                                            ↓
                                        </button>
                                        <button onClick={() => removeStep(step.id)}>
                                            <IconTrash />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.addStepForm}>
                            <h4>Añadir nuevo paso</h4>
                            <input
                                type="text"
                                placeholder="Nombre del paso"
                                value={newStep.name}
                                onChange={(e) => setNewStep(prev => ({ ...prev, name: e.target.value }))}
                                className={styles.stepInput}
                            />
                            
                            <select
                                value={newStep.type}
                                onChange={(e) => setNewStep(prev => ({ ...prev, type: e.target.value }))}
                                className={styles.stepSelect}
                            >
                                <option value="action">Acción</option>
                                <option value="condition">Condición</option>
                                <option value="loop">Bucle</option>
                                <option value="variable">Variable</option>
                            </select>
                            
                            <button onClick={addStep} className={styles.addStepButton}>
                                <IconPlus /> Añadir Paso
                            </button>
                        </div>
                    </div>

                    <div className={styles.templateActions}>
                        <button onClick={saveTemplate} className={styles.saveButton}>
                            <IconCheck /> Guardar Template
                        </button>
                        <button 
                            onClick={() => onStatusChange('example')} 
                            className={styles.executeButton}
                            disabled={template.steps.length === 0}
                        >
                            <IconPlay /> Ejecutar Scraping
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ===== ESTADO EXAMPLE =====
    const ExampleState = () => {
        const executeScraping = async () => {
            setIsExecuting(true);
            const startTime = Date.now();
            
            try {
                // Simular ejecución del scraping
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                // Simular resultados
                const mockResults = selectors.map(selector => ({
                    id: uuidv4(),
                    selector: selector.name,
                    value: `Valor extraído de ${selector.name}`,
                    status: 'success',
                    timestamp: new Date().toISOString()
                }));
                
                setScrapingResults(mockResults);
                setExecutionTime((Date.now() - startTime) / 1000);
                
            } catch (error) {
                console.error('Error executing scraping:', error);
            } finally {
                setIsExecuting(false);
            }
        };

        return (
            <div className={styles.exampleContainer}>
                <div className={styles.exampleHeader}>
                    <h3>📊 Resultados del Scraping</h3>
                    <p>Ejecuta el template y visualiza los resultados</p>
                </div>

                <div className={styles.exampleContent}>
                    <div className={styles.executionControls}>
                        <button
                            onClick={executeScraping}
                            disabled={isExecuting}
                            className={styles.executeButton}
                        >
                            {isExecuting ? <IconPause /> : <IconPlay />}
                            {isExecuting ? 'Ejecutando...' : 'Ejecutar Scraping'}
                        </button>
                        
                        {executionTime > 0 && (
                            <div className={styles.executionTime}>
                                Tiempo de ejecución: {executionTime.toFixed(2)}s
                            </div>
                        )}
                    </div>

                    <div className={styles.resultsContainer}>
                        <h4>Resultados ({scrapingResults.length})</h4>
                        
                        {scrapingResults.length > 0 ? (
                            <div className={styles.resultsTable}>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Selector</th>
                                            <th>Valor</th>
                                            <th>Estado</th>
                                            <th>Timestamp</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scrapingResults.map(result => (
                                            <tr key={result.id}>
                                                <td>{result.selector}</td>
                                                <td>{result.value}</td>
                                                <td>
                                                    <span className={`${styles.status} ${styles[result.status]}`}>
                                                        {result.status}
                                                    </span>
                                                </td>
                                                <td>{new Date(result.timestamp).toLocaleTimeString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className={styles.noResults}>
                                <p>No hay resultados aún. Ejecuta el scraping para ver los datos extraídos.</p>
                            </div>
                        )}
                    </div>

                    <div className={styles.exampleActions}>
                        <button onClick={() => onStatusChange('template')} className={styles.backButton}>
                            ← Volver al Template
                        </button>
                        <button onClick={() => onStatusChange('selector')} className={styles.newScrapingButton}>
                            🆕 Nuevo Scraping
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Renderizado principal
    const renderCurrentState = () => {
        switch (status) {
            case 'loading':
                return <LoadingState />;
            case 'selector':
                return <SelectorState />;
            case 'template':
                return <TemplateState />;
            case 'example':
                return <ExampleState />;
            default:
                return (
                    <div className={styles.errorContainer}>
                        <h3>❌ Estado no válido</h3>
                        <p>El estado "{status}" no es válido.</p>
                        <button onClick={() => onStatusChange('loading')}>
                            Volver al inicio
                        </button>
                    </div>
                );
        }
    };

    return (
        <div className={styles.scrapingContainer}>
            <div className={styles.statusIndicator}>
                <div className={styles.statusSteps}>
                    <div className={`${styles.statusStep} ${status === 'loading' ? styles.active : ''}`}>
                        1. Cargar
                    </div>
                    <div className={`${styles.statusStep} ${status === 'selector' ? styles.active : ''}`}>
                        2. Seleccionar
                    </div>
                    <div className={`${styles.statusStep} ${status === 'template' ? styles.active : ''}`}>
                        3. Template
                    </div>
                    <div className={`${styles.statusStep} ${status === 'example' ? styles.active : ''}`}>
                        4. Ejecutar
                    </div>
                </div>
            </div>
            
            {renderCurrentState()}
        </div>
    );
};

export default ScrapingContainer;